/** @type {import('../help-index.js').HelpSidebarItem} */
export const SETTINGS_VOUCHERS_LIST_ITEM = {
  id: 'sub_nav_0',
  screenId: 'settings-vouchers-list',
  label: 'Comprobantes',
  description: 'Tipos de comprobante del sistema (facturas, remitos, recibos, etc.).',
  detail: {
    sections: [
      {
        title: 'Para qué sirve',
        intro: 'Definís los tipos de comprobante que usa la empresa: descripción, abreviatura, si es fiscal, si discrimina o genera IVA, y el código ARCA para factura electrónica.',
      },
      {
        title: 'Paso 1 — Lista',
        bullets: [
          'Interruptor «Comprobantes activos» para filtrar.',
          'Columnas: Tipo, ARCA, Descripción, Abrev., Fiscal, Disc. IVA, Gen. IVA, Activo.',
          'Tocá una fila para ver detalle y cambiar si está activo.',
          'Botón + — nuevo comprobante.',
        ],
      },
      {
        title: 'Paso 2 — Nuevo comprobante',
        fields: [
          { label: 'Tipo base', description: 'Plantilla del sistema (factura venta, remito, etc.). Obligatorio.' },
          { label: 'Tipo comprobante ARCA', description: 'Opcional; vincula con AFIP para electrónicos.' },
          { label: 'Descripción / Abreviatura', description: 'Obligatorias; la abreviatura aparece en listas.' },
          { label: 'Fiscal / Discrimina IVA / Genera IVA / Activo', description: 'Interruptores según el uso impositivo.' },
        ],
        paragraphs: ['Al crear, el sistema asigna un número de tipo automáticamente.'],
      },
      {
        title: 'Paso 3 — Editar',
        paragraphs: [
          'Solo podés cambiar el interruptor Activo. El resto de los datos quedan en solo lectura.',
        ],
      },
    ],
  },
}

/** @type {import('../help-index.js').HelpSidebarItem} */
export const SETTINGS_VOUCHERS_POS_ITEM = {
  id: 'sub_nav_1',
  screenId: 'settings-vouchers-pos',
  label: 'Puntos de ventas',
  description: 'Numeración de puntos de venta (POS) para comprobantes fiscales y electrónicos.',
  detail: {
    sections: [
      {
        title: 'Para qué sirve',
        intro: 'Cada punto de venta es un número que AFIP reconoce al facturar. Configurás si es fiscal, si emite comprobante electrónico y si está activo.',
      },
      {
        title: 'Paso 1 — Lista',
        bullets: [
          'Interruptor «Puntos de venta activos».',
          'Columnas: ID, Fiscal, Comprobante electrónico, POS, Activo, fechas y usuarios.',
          'Tocá una fila para ver detalle.',
          'Botón + — nuevo punto de venta.',
        ],
      },
      {
        title: 'Paso 2 — Nuevo punto de venta',
        fields: [
          { label: 'Número de POS', description: 'Entero mayor a cero; debe coincidir con el habilitado en AFIP.' },
          { label: 'Fiscal', description: 'Indica si el POS es para comprobantes fiscales.' },
          { label: 'Comprobante electrónico', description: 'Activalo si ese POS factura por webservice AFIP.' },
        ],
      },
      {
        title: 'Paso 3 — Editar',
        paragraphs: ['Solo podés activar o desactivar el POS. El número y las opciones fiscales no se modifican desde aquí.'],
      },
    ],
  },
}

/** @type {import('../help-index.js').HelpSidebarItem} */
export const SETTINGS_VOUCHERS_POS_MATCH_ITEM = {
  id: 'sub_nav_2',
  screenId: 'settings-vouchers-pos-match',
  label: 'POS y tipos',
  description: 'Qué tipos de comprobante puede emitir cada punto de venta.',
  detail: {
    sections: [
      {
        title: 'Para qué sirve',
        intro: 'Un mismo POS puede tener varios tipos de comprobante (por ejemplo Factura A y Nota de crédito). Acá definís esas combinaciones.',
      },
      {
        title: 'Antes de empezar',
        paragraphs: [
          'Tené cargados puntos de venta activos y tipos de comprobante de venta, remito o recibo de cobranza.',
        ],
      },
      {
        title: 'Paso 1 — Ver relaciones',
        bullets: [
          'La lista muestra POS, tipo, descripción, abreviatura y datos fiscales del comprobante.',
          'Última columna: ícono de papelera para quitar la relación (pide confirmación).',
        ],
      },
      {
        title: 'Paso 2 — Agregar relación',
        steps: [
          'Tocá el botón +.',
          'Elegí Punto de venta y Tipo de comprobante en los desplegables.',
          'Tocá «Agregar relación».',
        ],
      },
    ],
  },
}

/** @type {import('../help-index.js').HelpSidebarItem} */
export const SETTINGS_VOUCHERS_AFIP_TYPES_ITEM = {
  id: 'sub_nav_3',
  screenId: 'settings-vouchers-afip-types',
  label: 'Tipos ARCA',
  description: 'Catálogo local de códigos de comprobante AFIP/ARCA.',
  detail: {
    sections: [
      {
        title: 'Para qué sirve',
        intro: 'Registrás en el sistema los códigos oficiales de AFIP (Factura A, B, C, etc.) para usarlos al crear comprobantes y al facturar electrónicamente.',
      },
      {
        title: 'Paso 1 — Lista',
        bullets: [
          'Columnas: ID y Descripción.',
          'Solo consulta; no se edita desde la lista.',
        ],
      },
      {
        title: 'Paso 2 — Nuevo tipo ARCA',
        steps: [
          'Tocá el botón +.',
          'El sistema consulta ARCA y muestra los tipos disponibles.',
          'Elegí uno del desplegable y tocá «Confirmar».',
        ],
        paragraphs: [
          'Si el tipo ya estaba registrado, el sistema avisará el error.',
        ],
      },
    ],
  },
}

/** @type {import('../help-index.js').HelpSidebarItem} */
export const SETTINGS_VOUCHERS_CURRENCIES_ITEM = {
  id: 'sub_nav_4',
  screenId: 'settings-vouchers-currencies',
  label: 'Monedas comprobantes',
  description: 'Qué monedas admite cada tipo de comprobante.',
  detail: {
    sections: [
      {
        title: 'Para qué sirve',
        intro: 'Indicás en qué monedas se puede emitir o registrar cada comprobante (por ejemplo factura en pesos y en dólares).',
      },
      {
        title: 'Antes de empezar',
        paragraphs: [
          'Configurá monedas en Configuración → Monedas y tipos de comprobante en la primera pestaña.',
        ],
      },
      {
        title: 'Paso 1 — Ver relaciones',
        bullets: [
          'Columnas: Tipo, Comprobante, Moneda, Descripción moneda.',
          'Ícono de papelera — quitar la moneda de ese comprobante (con confirmación).',
        ],
      },
      {
        title: 'Paso 2 — Agregar moneda',
        steps: [
          'Tocá el botón +.',
          'Elegí Categoría: Comprobantes de Venta o Comprobantes de Compra.',
          'Seleccioná el tipo de comprobante y la moneda.',
          'Tocá «Confirmar».',
        ],
      },
    ],
  },
}
